import {
    addDays,
    differenceInCalendarDays,
    format,
    startOfDay,
    startOfMonth,
    startOfWeek,
} from 'date-fns'

import {businessCalendarDate} from 'src/api/business-time'

import {toLocalDate} from '../jobs/job-schedule-format'
import {MONTH_VIEW_DAYS} from './timeline-view'
import type {ViewRange} from './types'

export type BoardScale = 'week' | 'month'

const WEEK_STARTS_ON = 0 as const

export function boardScaleForViewRange(viewRange: ViewRange): BoardScale {
    return viewRange === 'month' ? 'month' : 'week'
}

export function boardStepDays(scale: BoardScale): number {
    return scale === 'month' ? MONTH_VIEW_DAYS : 7
}

/** Calendar dates at local noon avoid UTC day-shift in Mantine pickers. */
export function calendarDateForPicker(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0)
}

/** Mantine date pickers often emit UTC-midnight dates for calendar days. */
export function normalizePickerDate(value: Date | string): Date {
    if (typeof value === 'string') {
        return calendarDateForPicker(toLocalDate(value))
    }

    return calendarDateForPicker(
        new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()),
    )
}

export function mondayOfWeekContaining(date: Date): Date {
    return startOfWeek(startOfDay(date), {weekStartsOn: WEEK_STARTS_ON})
}

export function weekStartFromWeekOffset(weekOffset: number, today = businessCalendarDate()): Date {
    return addDays(mondayOfWeekContaining(today), weekOffset * 7)
}

export function weekOffsetFromDate(date: Date | string, today = businessCalendarDate()): number {
    const monday = mondayOfWeekContaining(normalizePickerDate(date))
    const currentMonday = mondayOfWeekContaining(today)
    return Math.max(0, Math.floor(differenceInCalendarDays(monday, currentMonday) / 7))
}

/** Days to shift canonical board data before projection. */
export function anchorDayShiftForView(
    viewRange: ViewRange,
    anchorOffset: number,
    today = businessCalendarDate(),
): number {
    if (viewRange === 'week') {
        const weekStart = weekStartFromWeekOffset(anchorOffset, today)
        return Math.max(0, differenceInCalendarDays(weekStart, today))
    }

    return anchorOffset
}

export function anchorDateFromOffset(anchorOffset: number, today = businessCalendarDate()): Date {
    return addDays(today, anchorOffset)
}

export function anchorOffsetFromDate(date: Date | string, today = businessCalendarDate()): number {
    const normalized =
        typeof date === 'string' ? normalizePickerDate(date) : calendarDateForPicker(date)
    return Math.max(0, differenceInCalendarDays(startOfDay(normalized), today))
}

export function weekRangeFromWeekOffset(
    weekOffset: number,
    today = businessCalendarDate(),
): [Date, Date] {
    const start = calendarDateForPicker(weekStartFromWeekOffset(weekOffset, today))
    return [start, calendarDateForPicker(addDays(start, 6))]
}

export function monthRangeFromOffset(
    anchorOffset: number,
    today = businessCalendarDate(),
): [Date, Date] {
    const start = calendarDateForPicker(anchorDateFromOffset(anchorOffset, today))
    return [start, calendarDateForPicker(addDays(start, MONTH_VIEW_DAYS - 1))]
}

export function monthPickerValueFromOffset(
    anchorOffset: number,
    today = businessCalendarDate(),
): Date {
    return calendarDateForPicker(anchorDateFromOffset(anchorOffset, today))
}

export function anchorOffsetFromMonthPick(
    date: Date | string,
    today = businessCalendarDate(),
): number {
    const picked = normalizePickerDate(date)
    const isCurrentMonth =
        picked.getFullYear() === today.getFullYear() && picked.getMonth() === today.getMonth()

    if (isCurrentMonth) {
        return 0
    }

    const monthStart = calendarDateForPicker(startOfMonth(picked))
    if (monthStart < startOfDay(today)) {
        return 0
    }

    return differenceInCalendarDays(monthStart, today)
}

export function weekNavigationLabel(weekOffset: number, today = businessCalendarDate()): string {
    const [start, end] = weekRangeFromWeekOffset(weekOffset, today)
    const range = `${format(start, 'MMM d')} – ${format(end, 'MMM d')}`

    if (weekOffset === 0) {
        return `This week · ${range}`
    }
    return range
}

export function monthNavigationLabel(anchorOffset: number, today = businessCalendarDate()): string {
    const month = monthPickerValueFromOffset(anchorOffset, today)
    const label = format(month, 'MMMM yyyy')

    if (anchorOffset === 0) {
        return `This month · ${label}`
    }
    return label
}

/** Earliest selectable calendar day in the board picker. */
export function pickerMinDate(scale: BoardScale, today = businessCalendarDate()): Date {
    if (scale === 'month') {
        return today
    }

    // Keep the full current week selectable (including days before today) while
    // still blocking navigation into prior weeks.
    return weekRangeFromWeekOffset(0, today)[0]
}

export function navigationDateLabel(
    scale: BoardScale,
    anchorOffset: number,
    today = businessCalendarDate(),
): string {
    return scale === 'month'
        ? monthNavigationLabel(anchorOffset, today)
        : weekNavigationLabel(anchorOffset, today)
}
